/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {fromEvent, merge, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, startWith} from 'rxjs/operators';

@Directive({
  selector: '[gp-stick-between]'
})
export class StickBetweenDirective implements AfterViewInit, OnDestroy {
  @Input('gp-stick-between')
  public between: { top?: number, bottom?: number } = {}; // between document top(px) and bottom(px)

  @Output()
  public onResize: EventEmitter<number> = new EventEmitter<number>();

  // Placeholder element that will be used to 'replace' the sticky element so that it fills the same space
  private placeholder: HTMLElement;

  private resizeEvtObs: Observable<any>;
  private scrollEvtObs: Observable<any>;
  private subscription: Subscription;

  constructor(private el: ElementRef) {
  }

  public ngAfterViewInit() {
    this.placeholder = document.createElement('div');

    this.insertAfter(this.placeholder, this.el.nativeElement);
    this.initListeners();
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private setStyles(el, rect: any = {}, fixed: boolean = false) {
    const height: string = typeof rect.height === 'number' ? `${rect.height}px` : '';
    const width: string = typeof rect.width === 'number' ? `${rect.width}px` : '';
    const position: string = fixed ? 'fixed' : '';
    const zIndex: number = fixed ? 101 : 0;
    const top: string = fixed ? `${this.between.top}px` : '';

    Object.assign(el.style, {
      height,
      width,
      position,
      zIndex,
      top
    });
  }

  private initListeners() {
    this.resizeEvtObs = fromEvent(window, 'resize');
    this.scrollEvtObs = fromEvent(window, 'scroll');

    this.subscription = merge(this.resizeEvtObs, this.scrollEvtObs)
      .pipe(
        startWith(null),
        // .debounceTime(100)
        map(() => {
          this.setStyles(this.el.nativeElement, {}, false);
          this.placeholder.style.display = 'none';
        }),
        map(() => this.el.nativeElement.getBoundingClientRect()),
        filter((rect) => rect.height && (rect.top <= this.between.top || rect.top >= this.between.bottom)),
        map((rect) => {
          this.setStyles(this.el.nativeElement, rect, true);
          this.placeholder.style.display = 'block';
          this.setStyles(this.placeholder, rect);

          return rect.height;
        }),
        distinctUntilChanged(),
        map((height) => this.onResize.next(height))
      )
      .subscribe();
  }

  private insertAfter(elem, refElem) {
    const parent = refElem.parentNode;
    const next = refElem.nextSibling;
    if (next) {
      return parent.insertBefore(elem, next);
    } else {
      return parent.appendChild(elem);
    }
  }
}
